import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Box,
	Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { PricingItem } from 'common/types';
import errorHandler from 'common/services/errorHandler';
import { sortPricingItems } from 'common/utils/pricing';
import Spinner from 'common/components/Spinner';
import MoneyTextField from 'common/components/MoneyTextField';
import useFormField, { Form, validateForm } from 'common/hooks/useFormField';
import PricingTableBody from './PricingTableBody';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import { getItemsWithBasePrice } from 'views/Product/__utils/utils';
import { useTranslation } from 'services/localization/useTranslation';
import { removeIncorrectDbPathCharacters } from 'common/utils/validation';
import { FieldValues, BatchManager } from 'common/frontend/firebase/firestore';
import { api } from 'common/frontend/api';
import Banner from 'components/Banner';

interface Props {
	tableName: string | undefined;
	items: PricingItem[] | undefined;
	basePrice?: number;
	productId?: string;
	onClose: () => void;
	onCreateOrUpdateTable: (tableName: string) => void;
}

type PricingTableForm = {
	items: PricingItem[];
	name: string;
};

const PricingTableEditModal = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const shopId = useSelector(ShopSelectors.activeShopId);
	const currency = useSelector(ShopSelectors.getShopCurrency);
	const pricingTables = useSelector(ShopSelectors.activeShopPricingTables);
	const pricingTableUsage = useSelector(StockSelectors.productsUsingPricingTables);

	const [loading, setLoading] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [basePrice, setBasePrice] = useState<number>(props.basePrice ?? 1000);

	const defaultItems = props.tableName ? pricingTables[props.tableName] : props.items ?? [];
	const defaultItemsWithExamplePrice = getItemsWithBasePrice(defaultItems, basePrice);
	const form: Form<PricingTableForm> = {
		items: useFormField<PricingItem[]>(defaultItemsWithExamplePrice, (items) => {
			if (items.length === 0) {
				return t(
					'pricing.pricingTemplateMustHaveOneRow',
					'Your pricing template must have at least one row',
				);
			}

			return;
		}),
		name: useFormField<string>(props.tableName ?? '', (tableName) => {
			if (!tableName || tableName.length === 0) {
				return t('pricing.pricingTemplateMustHaveName', 'Please give your template a name');
			}

			if (tableName !== props.tableName && existingTableNames.includes(tableName)) {
				return t(
					'pricing.pricingTemplateMustHaveUniqueName',
					'You already have another template with that name',
				);
			}

			return;
		}),
	};

	const existingTableNames = Object.keys(pricingTables);
	const isExistingTable = props.tableName && existingTableNames.includes(props.tableName);
	const usedByProducts = props.tableName ? pricingTableUsage[props.tableName] ?? [] : [];

	const handleTableNameChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
			const validTableName = removeIncorrectDbPathCharacters(e.target.value);
			form.name.handleChange(validTableName);
		},
		[form.name],
	);

	const handleBasePriceChange = useCallback(
		(newBasePrice) => {
			form.items.handleChange(getItemsWithBasePrice(form.items.value, newBasePrice));
			setBasePrice(newBasePrice);
		},
		[form.items],
	);

	const handleItemsChange = useCallback(
		(newItems: PricingItem[]) => {
			form.items.handleChange(newItems);
		},
		[form.items],
	);

	const handleSave = async () => {
		const { values, errors } = validateForm(form);
		if (errors.length > 0) return;
		setLoading(true);
		try {
			await createOrUpdatePricingTable(values.items, values.name);
			if (isExistingTable) {
				await updateProductPricingTables(values.name);
				props.onCreateOrUpdateTable(values.name);
				props.onClose();
				if (values.name !== props.tableName) {
					await removeRenamedPricingTable(props.tableName);
				}
			} else {
				props.onCreateOrUpdateTable(values.name);
				props.onClose();
			}
		} catch (err) {
			errorHandler.report(err);
			setSaveError(
				t(
					'common:states.somethingWentWrongWhileSaving',
					'Something went wrong while saving your changes',
				),
			);
		} finally {
			setLoading(false);
		}
	};

	const createOrUpdatePricingTable = async (items: PricingItem[], name: string) => {
		if (!shopId) return;
		let pricingTable = items.slice().sort(sortPricingItems);

		await api()
			.shops.doc(shopId)
			.public.update({
				[`pricingTables.${name}` as `pricingTables.${123}`]: pricingTable,
			});
	};

	const removeRenamedPricingTable = async (oldName: string | undefined) => {
		if (!oldName || !shopId) return;
		await api()
			.shops.doc(shopId)
			.public.update({
				[`pricingTables.${oldName}` as `pricingTables.${123}`]: FieldValues.delete(),
			});
	};

	const updateProductPricingTables = async (name: string) => {
		const ids = usedByProducts.map((p) => p.id);
		if (ids.length === 0) return;
		const batchManager = new BatchManager();

		ids.forEach((id) => {
			api(batchManager.batch()).products.doc(id).update({
				pricing: [],
				useSavedPricingTable: true,
				savedPricingTable: name,
			});
		});

		await batchManager.commit();
	};

	const renderAlert = () => {
		const names = usedByProducts.filter((p) => p.id !== props.productId).map((p) => p.name);

		if (names.length > 0) {
			return (
				<Banner
					variant="warning"
					title={t(
						'pricing.pricingTemplateIsUsedByProducts',
						'This pricing template is used by the following products:',
					)}
					description={
						<Box>
							<ul>
								<li>{names.join(', ')}</li>
							</ul>
							{t(
								'pricing.pricingTemplateIsUsedByProductsDescription',
								'Any changes you make below will also update the pricing structure of the above products. If you want to only edit the pricing of an individual product, you can unlink it from this template.',
							)}
						</Box>
					}
				/>
			);
		}
		return null;
	};

	return (
		<Dialog
			open={true}
			onClose={props.onClose}
			closeAfterTransition
			aria-labelledby="pricingtable-edit-dialog-title"
			fullWidth
			maxWidth="lg"
		>
			<DialogTitle id="pricingtable-edit-dialog-title">
				{isExistingTable
					? t('pricing.editTemplate')
					: t('pricing.createNewTemplate', 'Create new template')}
			</DialogTitle>
			<DialogContent>
				{renderAlert()}
				<Box mt={2} mb={2} display="flex" flexDirection="row" flexWrap="wrap">
					<TextField
						label={t('pricing.templateName', 'Template name')}
						variant="outlined"
						margin="normal"
						value={form.name.value}
						onChange={handleTableNameChange}
						error={Boolean(form.name.error)}
						helperText={form.name.error}
						className={classes.textField}
					/>
					<MoneyTextField
						currency={currency}
						label={t('pricing.exampleBasePrice', 'Example base price')}
						value={basePrice}
						useMinorUnits
						onValueChange={handleBasePriceChange}
						className={classes.textField}
						variant="outlined"
						helperText={t(
							'pricing.exampleBasePriceDescription',
							'Try the template with different base prices',
						)}
						margin="normal"
					/>
				</Box>
				{currency && (
					<PricingTableBody
						items={form.items.value}
						basePrice={basePrice}
						onChange={handleItemsChange}
						currency={currency}
						priceIsExample
					/>
				)}
				{Boolean(form.items.error) && <Typography color="error">{form.items.error}</Typography>}
			</DialogContent>
			<DialogActions className={classes.actions}>
				{Boolean(saveError) && (
					<Box m={1}>
						<Typography variant="body1" color="error" align="center">
							{saveError}
						</Typography>
					</Box>
				)}
				<Box display="flex" flexDirection="row" justifyContent="center">
					<Button onClick={props.onClose}>{t('common:actions.cancel')}</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={handleSave}
						disabled={Boolean(form.name.error) || Boolean(form.items.error)}
					>
						{loading ? (
							<Spinner color="white" size={20} />
						) : isExistingTable ? (
							t('pricing.saveTemplate', 'Save template')
						) : (
							t('pricing.createTemplate', 'Create template')
						)}
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		textField: {
			width: '100%',
			maxWidth: '300px',
			marginRight: theme.spacing(1),
		},
		actions: {
			padding: theme.spacing(2),
			background: theme.palette.background.secondary,
			display: 'flex',
			flexWrap: 'wrap',
			flexDirection: 'row',
			justifyContent: 'flex-end',
		},
		showAllProductsLink: {
			textDecoration: 'underline',
		},
	}),
);

export default PricingTableEditModal;
